import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import _ from "lodash";
import { Actions } from "../core/modules/Actions";
import { getText } from "../core/Constant";
import DocumentRepository from "../core/repository/DocumentRepository";

export const useQuizForm = (activeQuizData = null, initialFolderId = null, initialCourseId = null) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [isNameError, setNameError] = useState(false);

  const [description, setDescription] = useState("");
  const [isDescriptionError, setDescriptionError] = useState(false);

  const [noAttempt, setNoAttempt] = useState("");
  const [isNoAttemptError, setNoAttemptError] = useState(false);

  const [time, setTime] = useState("");
  const [isTimeError, setTimeError] = useState(false);

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const [isCourseError, setCourseError] = useState(false);

  // Folders and Filtering
  const [folderSearch, setFolderSearch] = useState("");
  const [folderItems, setFolderItems] = useState([]);
  
  const [selectedFolderIds, setSelectedFolderIds] = useState(() => {
    return initialFolderId !== null
      ? [Number(initialFolderId)]
      : [];
  });
  const courseFolderIds = useRef(new Set());
  const excludedCourseFolderIds = useRef(new Set());
  const latestSelectedCourseId = useRef(null);
  
  const [loadingFolders, setLoadingFolders] = useState(false);

  const clist =
    useSelector((state) => state.course.get("allStatusCourseList")) || [];

  useEffect(() => {
    dispatch(Actions.course.getAllStateCourseList());
    dispatch(Actions.quizes.getCourseWeekDataList());
  }, [dispatch]);

  useEffect(() => {
    if (!activeQuizData && initialFolderId !== null) {
      setSelectedFolderIds((prev) => {
        const numId = Number(initialFolderId);
        return prev.includes(numId) ? prev : [...prev, numId];
      });
    }
  }, [initialFolderId, activeQuizData]);

  useEffect(() => {
    if (!activeQuizData && initialCourseId !== null) {
      setSelectedCourses((prev) => {
        const courseId = Number(initialCourseId);
        return prev.includes(courseId) ? prev : [...prev, courseId];
      });
    }
  }, [initialCourseId, activeQuizData]);

  useEffect(() => {
    if (!activeQuizData) return;

      setName(activeQuizData.name ?? "");
      setDescription(activeQuizData.description ?? "");
      setTime(activeQuizData.duration ?? "");
      setNoAttempt(activeQuizData.no_of_attempts ?? "");

      const extractedThumbnail =
      activeQuizData.quiz_thumbnail_url ||
      activeQuizData.quiz_thumbnail ||
      activeQuizData.thumbnail ||
      activeQuizData.preview ||
        null;

      if (extractedThumbnail) {
        setThumbnail(extractedThumbnail);
      }

      if (Array.isArray(activeQuizData.selectedCourses)) {
        setSelectedCourses(activeQuizData.selectedCourses.map(Number));
      }

      const folderSet = new Set();
        const dirs = activeQuizData.directories || [];
        dirs.forEach((dir) => {
          const folderId = dir?.parent_id ?? dir?.id ?? dir?.directory_id;
          if (folderId != null) folderSet.add(Number(folderId));
      });

      if (initialFolderId !== null) {
        folderSet.add(Number(initialFolderId));
      }

      setSelectedFolderIds(Array.from(folderSet));
    }, [activeQuizData, initialFolderId]);

  useEffect(() => {
    const fetchFolders = async () => {
      const courseIds = selectedCourses?.length
        ? selectedCourses
        : clist?.map((c) => c.id) || [];
      if (!courseIds.length) {
        setFolderItems([]);
        return;
      }
      setLoadingFolders(true);
      try {
        const promises = courseIds.map((courseId) =>
          DocumentRepository.getCourseFolders(courseId)
        );

        const results = await Promise
          .all
          (promises);
        const combined = _.flatten(
          results.map((res) => _.get(res, "documents", res?.data || []))
        );
        const folders = _.uniqBy(
          combined.filter((doc) => Number(doc?.is_directory) === 1),
          "id",
        );
        setFolderItems(folders);
      } catch (err) {
        console.error("Error loading course folders", err);
        setFolderItems([]);
      } finally {
        setLoadingFolders(false);
      }
    };
    fetchFolders();
  }, [selectedCourses, clist]);

  const onChangeName = (e) => {
    const text = getText(e);
    setName(text);
    setNameError(false);
  };

  const onChangeDescription = (e) => {
    const text = getText(e);
    setDescription(text);
    setDescriptionError(false);
  };

  const onChangeAttempt = (e) => {
    setNoAttempt(getText(e));
    setNoAttemptError(false);
  };

  const onChangeTime = (e) => {
    setTime(getText(e));
    setTimeError(false);
  };

  const handleCourseChange = (event) => {
    const value = event.target.value;
    const rawArray = typeof value === "string" ? value.split(",") : value;
    excludedCourseFolderIds.current.clear();
    latestSelectedCourseId.current = null;
    setSelectedCourses(rawArray.map(Number));
    setCourseError(false);
  };

  const handleChipDelete = (id, folders = folderItems) => {
    if (Number(latestSelectedCourseId.current) === Number(id)) {
      latestSelectedCourseId.current = null;
    }
    const folderIdsForCourse = selectedFolderIds.filter(
      (folderId) =>
        Number(getFolderCourseId(folders, folderId)) === Number(id)
    );
    setSelectedFolderIds((prev) =>
      prev.filter((folderId) => !folderIdsForCourse.includes(folderId))
    );
    folderIdsForCourse.forEach((folderId) => {
      courseFolderIds.current.delete(Number(folderId));
      excludedCourseFolderIds.current.delete(Number(folderId));
    });
    setSelectedCourses((prev) => prev.filter((cId) => Number(cId) !== Number(id)));
    setCourseError(false);
  };

  const getFolderCourseId = (folders, folderId, inheritedCourseId = null) => {
    if (!folders) return null;

    const folderList = Array.isArray(folders)
      ? folders
      : Object.values(folders.quiz_directories || folders);

    for (const folder of folderList) {
      if (!folder) continue;

      const courseId = folder.course_id ?? folder.courseId ?? inheritedCourseId;
      if (String(folder.id) === String(folderId)) return courseId;

      const childCourseId = getFolderCourseId(folder.children, folderId, courseId);
      if (childCourseId != null) return childCourseId;
    }

    return null;
  };

  const toggleFolderSelection = (id, folders = []) => {
    const numId = Number(id);
    const isFolderSelected = selectedFolderIds.some(
      (folderId) => Number(folderId) === numId
    );
    if (isFolderSelected) {
      excludedCourseFolderIds.current.add(numId);
    } else {
      excludedCourseFolderIds.current.delete(numId);
    }
    courseFolderIds.current.delete(numId);
    setSelectedFolderIds((prev) =>
      prev.includes(numId)
        ? prev.filter((fId) => fId !== numId)
        : [...prev, numId]
    );

    const folderCourseId = getFolderCourseId(folders, id);
    if (!isFolderSelected && folderCourseId != null) {
      latestSelectedCourseId.current = Number(folderCourseId);
      setSelectedCourses((prev) =>
        prev.some((courseId) => Number(courseId) === Number(folderCourseId))
          ? prev
          : [...prev, Number(folderCourseId)]
      );
    } else if (isFolderSelected) {
      latestSelectedCourseId.current = null;
      const hasAnotherFolderForCourse = selectedFolderIds.some(
        (folderId) =>
          Number(folderId) !== numId &&
          Number(getFolderCourseId(folders, folderId)) === Number(folderCourseId)
      );

      if (!hasAnotherFolderForCourse) {
        setSelectedCourses((prev) =>
          prev.filter(
            (courseId) => Number(courseId) !== Number(folderCourseId)
          )
        );
      }
    }
  };

  const syncCourseFolderSelections = useCallback((folderIds) => {
    const nextCourseFolderIds = new Set(
      folderIds
        .map(Number)
        .filter((id) => !excludedCourseFolderIds.current.has(id))
    );
    setSelectedFolderIds((prev) => {
      const shouldPreservePreviousCourseFolders =
        latestSelectedCourseId.current != null;
      const retainedFolderIds = shouldPreservePreviousCourseFolders
        ? prev
        : prev.filter(
            (id) =>
              !courseFolderIds.current.has(Number(id)) ||
              nextCourseFolderIds.has(Number(id))
          );
      const nextFolderIds = Array.from(
        new Set([...retainedFolderIds, ...nextCourseFolderIds])
      );
      const hasChanged =
        nextFolderIds.length !== prev.length ||
        nextFolderIds.some((id, index) => Number(id) !== Number(prev[index]));

      return hasChanged ? nextFolderIds : prev;
    });
    courseFolderIds.current = nextCourseFolderIds;
  }, []);

  const filteredFolderItems = useMemo(() => {
    const search = folderSearch?.trim().toLowerCase();
    if (!search) return folderItems;
    return folderItems.filter((f) => f.name?.toLowerCase().includes(search));
  }, [folderItems, folderSearch]);

  // Form Validation Checklist
  const validateForm = () => {
    const isNameValid = name.trim().length > 0;
    const isDescriptionValid = description.trim().length > 0;
    const isAttemptValid = Number(noAttempt) > 0;
    const isTimeValid = Number(time) > 0;

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);
    setNoAttemptError(!isAttemptValid);
    setTimeError(!isTimeValid);

    const isFormValid = [isNameValid, isDescriptionValid, isAttemptValid, isTimeValid].every(Boolean);
    return isFormValid;
  };

  return {
    name,
    description,
    noAttempt,
    time,
    selectedCourses,
    isCourseError,
    setIsCourseError: setCourseError,
    clist,
    thumbnail,
    setThumbnail,
    setThumbnailRemoved,
    thumbnailRemoved,
    folderSearch,
    setFolderSearch,
    loadingFolders,
    filteredFolderItems,
    selectedFolderIds,
    isNameError,
    isDescriptionError,
    isNoAttemptError,
    isTimeError,
    onChangeName,
    onChangeDescription,
    onChangeAttempt,
    onChangeTime,
    handleCourseChange,
    handleChipDelete,
    toggleFolderSelection,
    validateForm,
    syncCourseFolderSelections,
    latestSelectedCourseId: latestSelectedCourseId.current,
  };
};
